import React, { useRef, useState, useEffect } from "react";

import { DataTable } from "primereact/datatable";
import { Panel } from "primereact/panel";
import { Dropdown } from "primereact/dropdown";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { OverlayPanel } from "primereact/overlaypanel";
import { formatDate } from "../../../utils/helpers";

import {
    JudgmentIntegrationCase,
    JudgmentTableProps,
    JudgmentIntegrationJudgment,
    GenerateNoticeCommand,
    GenerateDocketEventsCommand,
    IntegrationCategory,
    IntegrationCaseStatuses,
    ProcessStatus,
} from "../types/JudgmentIntegrationTypes";
import { InputText } from "primereact/inputtext";
import judgmentClient from "../Api/judgment-client";
import judgmentStore from "../stores/judgment-store";
import appSettingStore from "../stores/appSettings-store";
import { observer } from "mobx-react";
import { Http } from "utils/Http";
import { MenuItem } from "primereact/menuitem";
import { FilterMatchMode, FilterOperator } from "primereact/api";

export const JudgmentIntegrationTable: React.FC<JudgmentTableProps> = observer(
    (props) => {
        const dtJudgment = React.useRef<DataTable>(null);
        const headerMenu = React.useRef<Menu>(null);

        useEffect(() => {
            const civilErrors = judgmentStore.currentIntegration.civilEntries.filter(
                (e) => {
                    return e.isProperlyFormatted === false;
                }
            ).length;
            const aliasesErrors =
                judgmentStore.currentIntegration.aliasEntries.filter((e) => {
                    return e.isProperlyFormatted === false;
                }).length;

            if (civilErrors > 0 || aliasesErrors > 0) {
                props.showImportErrorMessage(true);
            }
            initFilters();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const [filters, setFilters] = useState(null);
        const [globalFilterValue, setGlobalFilterValue] = useState("");
        const onGlobalFilterChange = (e: any) => {
            const value = e.target.value;
            let _filters = { ...filters };
            _filters["global"].value = value;

            setFilters(_filters);
            setGlobalFilterValue(value);
        };

        const initFilters = () => {
            setFilters({
                // @ts-ignore: 'FilterMatchMode' only refers to a type, but is being used as a value here.  TS2693
                global: { value: null, matchMode: FilterMatchMode.CONTAINS },
                caseNumber: {
                    // @ts-ignore: 'FilterMatchMode' only refers to a type, but is being used as a value here.  TS2693
                    operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
                },
                civilCaseNumber: {
                    // @ts-ignore: 'FilterMatchMode' only refers to a type, but is being used as a value here.  TS2693
                    operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
                },
                "primaryDefendant.fullName": {
                    // @ts-ignore: 'FilterMatchMode' only refers to a type, but is being used as a value here.  TS2693
                    operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
                },
                "primaryDefendant.address.fullAddress": {
                    // @ts-ignore: 'FilterMatchMode' only refers to a type, but is being used as a value here.  TS2693
                    operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
                },
                statusMessage: {
                    // @ts-ignore: 'FilterMatchMode' only refers to a type, but is being used as a value here.  TS2693
                    operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
                },
            });
            setGlobalFilterValue("");
        };

        const enableDocketStatuses = [
            IntegrationCaseStatuses.CaseFound,
            IntegrationCaseStatuses.Created,
        ];
        const enablePrintExportStatuses = [
            IntegrationCaseStatuses.CaseFound,
            IntegrationCaseStatuses.Created,
            IntegrationCaseStatuses.DocketCreated,
        ];
        const disableCreateJudgmentsStatues = [
            IntegrationCaseStatuses.Created,
            IntegrationCaseStatuses.DocketCreated,
        ];
        const disableCreateSatisfactionsStatuses = [
            IntegrationCaseStatuses.Created,
            IntegrationCaseStatuses.DocketCreated,
        ];

        const aliasOverlayPanel = useRef<OverlayPanel>(null);
        const [aliasListConcatenated, setaliasListConcatenated] = useState("");
        const defendantBodyTemplate = (values: JudgmentIntegrationCase) => {
            let aliasList = values.defendants.filter((p) => !p.isPrimary);
            const aliasClick = (e: any) => {
                aliasOverlayPanel.current.toggle(e);
                setaliasListConcatenated(
                    aliasList
                        .map((e) => e.fullName.trim())
                        .join("<br />")
                        .toString()
                );
            };

            if (aliasList.length > 0)
                return (
                    <a href="javascript:" onClick={aliasClick}>
                        {values.primaryDefendant.fullName.trim()}&nbsp;
                        <i className="pi pi-users"></i>
                    </a>
                );

            return <>{values.primaryDefendant.fullName}</>;
        };

        const createCasesFromJudgments = async () => {
            const judgmentID = judgmentStore.currentIntegration?.id;

            const response = await judgmentClient.createCases({
                judgmentIntegrationID: judgmentID,
            });
            if (
                response.errors &&
                response.errors.length > 0 &&
                response.success === false
            ) {
                props.showToast(
                    "error",
                    "There was an error creating cases from the judgments. Please contact support if this problem persists.",
                    response.errors[0],
                    10000
                );
            } else {
                props.showToast("success", "Cases and judgments successfully created!");
            }
            judgmentStore.setCurrentIntegration(response.judgmentIntegration);
        };

        const createCaseFromJudgment = async (jintCaseID: number) => {
            const judgmentID = judgmentStore.currentIntegration?.id;

            const response = await judgmentClient.createCases({
                judgmentIntegrationID: judgmentID,
                judgmentIntegrationCaseID: jintCaseID,
            });
            if (
                response.errors &&
                response.errors.length > 0 &&
                response.success === false
            ) {
                props.showToast(
                    "error",
                    "There was an error creating the case from this judgment. Please contact support if this problem persists.",
                    response.errors[0]
                );
            } else {
                props.showToast("success", "Case and judgment successfully created!");
                judgmentStore.setCurrentIntegration(response.judgmentIntegration);
            }
            return response;
        };

        const generateNotices = async (
            noticeReportName: string,
            praecipeReportName: string,
            jintID?: number,
            jintCaseID?: number,
            caseNumber?: string
        ) => {
            const command: GenerateNoticeCommand = {
                judgmentIntegrationCaseID: jintCaseID,
                judgmentIntegrationID: jintID,
                noticeReportName: noticeReportName,
                praecipeReportName: praecipeReportName,
                caseNumber: caseNumber,
            };
            const response = await judgmentClient.generateNotices(command);
            if (
                response.success === false &&
                response.error &&
                response.error.length > 0
            ) {
                props.showToast(
                    "error",
                    "There was an error generating the judgment notices.",
                    response.error
                );
            } else {
                props.showToast("success", "Notices generated successfully!");
                Http.openNewTab(
                    `JudgmentIntegration/GetGeneratedPDF?fileName=${response.fileName}`
                );
            }
        };

        const generateSatisfactions = async () => {
            const jitID = judgmentStore.currentIntegration?.id;
            const response = await judgmentClient.createSatisfactions({
                integrationID: jitID,
                userID: 0,
            });
            if (
                response.errors &&
                response.errors.length > 0 &&
                response.success === false
            ) {
                props.showToast(
                    "error",
                    "There was an error creating satisfactions. Please contact support if this problem persists.",
                    response.errors[0]
                );
            } else {
                props.showToast("success", "Satisfactions created successfully!");
            }
            judgmentStore.setCurrentIntegration(response.judgmentIntegration);
        };

        const generateCaseSatisfaction = async (jintCaseID: number | null) => {
            const jitID = judgmentStore.currentIntegration?.id;
            const response = await judgmentClient.createSatisfactions({
                integrationID: jitID,
                jintCaseID: jintCaseID,
                userID: 0,
            });
            if (
                response.errors &&
                response.errors.length > 0 &&
                response.success === false
            ) {
                props.showToast(
                    "error",
                    "There was an error creating satisfactions. Please contact support if this problem persists.",
                    response.errors[0]
                );
            } else {
                props.showToast("success", "Satisfactions created successfully!");
            }
            judgmentStore.setCurrentIntegration(response.judgmentIntegration);
        };

        const generateDocketEntries = async (
            noticeReportName: string,
            praecipeReportName: string,
            noticeCaseEventCategoryApplicationSettingName: string,
            praecipeCaseEventCategoryApplicationSettingName: string,
            jintID?: number,
            jintCaseID?: number
        ) => {
            const command: GenerateDocketEventsCommand = {
                judgmentIntegrationCaseID: jintCaseID,
                judgmentIntegrationID: jintID,
                noticeReportName: noticeReportName,
                praecipeReportName: praecipeReportName,
                noticeCaseEventCategoryApplicationSettingName:
                    noticeCaseEventCategoryApplicationSettingName,
                praecipeCaseEventCategoryApplicationSettingName:
                    praecipeCaseEventCategoryApplicationSettingName,
            };
            const response = await judgmentClient.generateDocketEvents(command);

            if (
                response.success === false &&
                response.error &&
                response.error.length > 0
            ) {
                props.showToast(
                    "error",
                    "There was an error generating docket events.",
                    response.error
                );
            } else {
                props.showToast("success", "Docket Events created successfully!");
                judgmentStore.setCurrentIntegration(response.integration);
            }
        };

        const downloadAllDocuments = async (
            judgmentIntegrationID: number,
            noticeReportName: string,
            praecipeReportName: string
        ) => {
            const response = await judgmentClient.getAllDocuments({
                judgmentIntegrationID: judgmentIntegrationID,
                judgmentIntegrationCaseID: null,
                noticeReportName: noticeReportName,
                praecipeReportName: praecipeReportName,
                caseNumber: null,
            });
            if (
                response.success === false &&
                response.error &&
                response.error.length > 0
            ) {
                props.showToast(
                    "error",
                    "There was an error generating the judgment notices.",
                    response.error
                );
            } else {
                props.showToast("success", "Notices generated successfully!");
                Http.openNewTab(
                    `JudgmentIntegration/GetGeneratedPDF?fileName=${response.fileName}`
                );
            }
        };

        const judgmentBodyTemplate = (values: JudgmentIntegrationJudgment[]) => {
            let rows = values.map((element) => {
                return (
                    <div key={`${element.ID}_judgment`}>
                        <div key={`${element.ID}_amount`}>
                            Amount:{" "}
                            {element.amount.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                            })}
                        </div>
                        <div key={`${element.ID}date`}>
                            Entry Date: {formatDate(element.filingDate.toString())}
                        </div>
                    </div>
                );
            });
            return rows;
        };

        const civilCaseNumberBodyTemplate = (value: JudgmentIntegrationCase) => {
            if (value.civilCaseNumber) {
                const link = `${props.casePageUrl}?caseId=${value.civilCaseID}`;

                return (
                    <div>
                        <a href={link} target="_blank" rel="noopener noreferrer">
                            {value.civilCaseNumber}
                        </a>
                    </div>
                );
            } else {
                if (judgmentStore.currentIntegration?.integrationCategoryID === IntegrationCategory.Satisfaction
                    && value.status == statuses.find(p => p.label == "Case Not Found").value
                ) {

                    var caseCreationEnabled = appSettingStore.settings.ApplicationSettings.find(p => p.Name == 'JudgmentIntegrationSatisfactionCaseCreationEnabled');
                    if (caseCreationEnabled && caseCreationEnabled.Value == true) {
                        return (<div><button className="btn btn-primary"
                            title="Create a new case, add the judgment, and docket the notices"
                            onClick={() => createCaseFromSatisfaction(value.judgmentIntegrationID, value.id)}>Generate</button>
                        </div>)
                    }
                }

                return null;
            }
        };

        const createCaseFromSatisfaction = async (jintId: number, jintCaseID: number | null) => {
            var res = await judgmentClient.createSatisfactionCasesAndDockets({
                JudgmentIntegrationID: jintId,
                JudgmentIntegrationCaseId: jintCaseID
            });

            judgmentStore.setCurrentIntegration(res.judgmentIntegration);
        };


        const actionsColumnTemplate = (value: JudgmentIntegrationCase) => {
            if (
                judgmentStore.currentIntegration?.integrationCategoryID ===
                IntegrationCategory.Judgment
            ) {
                return (
                    <div className="table-actions">
                        <Button
                            className="p-button-sm"
                            icon="pi pi-briefcase"
                            title="Create Judgment"
                            onClick={() => createCaseFromJudgment(value.id)}
                            disabled={disableCreateJudgmentsStatues.some(
                                (x) => x === value.status
                            )}
                        />
                        <Button
                            className="p-button-sm"
                            icon="pi pi-file"
                            title="Create Docket Event"
                            onClick={() =>
                                generateDocketEntries(
                                    "JudgmentIntegrationNotice",
                                    "PraecipeToEnterJudment",
                                    "JudgmentIntegrationCaseEventCategory",
                                    "JudgmentIntegrationJudgmentPraecipeCaseEventCategory",
                                    judgmentStore.currentIntegration?.id,
                                    value.id
                                )
                            }
                            disabled={!enableDocketStatuses.some((x) => x === value.status)}
                        />
                        <Button
                            className="p-button-sm"
                            icon="pi pi-download"
                            title="Export Documents"
                            onClick={() =>
                                generateNotices(
                                    "JudgmentIntegrationNotice",
                                    "PraecipeToEnterJudment",
                                    null,
                                    value.id,
                                    value.caseNumber
                                )
                            }
                            disabled={
                                !enablePrintExportStatuses.some((x) => x === value.status)
                            }
                        />
                    </div>
                );
            }

            //satisfactions
            if (
                judgmentStore.currentIntegration?.integrationCategoryID ===
                IntegrationCategory.Satisfaction
            ) {
                return (
                    <div className="table-actions">
                        <Button
                            className="p-button-sm"
                            icon="pi pi-briefcase"
                            title="Create Satisfaction"
                            onClick={() => generateCaseSatisfaction(value.id)}
                            disabled={disableCreateSatisfactionsStatuses.some(
                                (x) => x === value.status
                            )}
                        />
                        <Button
                            className="p-button-sm"
                            icon="pi pi-file"
                            title="Create Docket Event"
                            onClick={() =>
                                generateDocketEntries(
                                    "SatisfactionIntegrationNotice",
                                    "PraecipeToEnterSatisfaction",
                                    "JudgmentIntegrationSatisfactionCaseEventCategory",
                                    "JudgmentIntegrationSatisfactionPraecipeCaseEventCategory",
                                    judgmentStore.currentIntegration?.id,
                                    value.id
                                )
                            }
                            disabled={!enableDocketStatuses.some((x) => x === value.status)}
                        />
                        <Button
                            className="p-button-sm"
                            icon="pi pi-download"
                            title="Export Documents"
                            onClick={() =>
                                generateNotices(
                                    "SatisfactionIntegrationNotice",
                                    "PraecipeToEnterSatisfaction",
                                    null,
                                    value.id,
                                    value.caseNumber
                                )
                            }
                            disabled={
                                !enablePrintExportStatuses.some((x) => x === value.status)
                            }
                        />
                    </div>
                );
            }
        };

        const headerTemplate = () => {
            if (
                judgmentStore.currentIntegration?.integrationCategoryID ===
                IntegrationCategory.Judgment
            ) {
                return judgmentHeaderTemplate();
            }
            if (
                judgmentStore.currentIntegration?.integrationCategoryID ===
                IntegrationCategory.Satisfaction
            ) {
                return satisfactionsHeaderTemplate();
            }
        };

        //disable when there is no "case is not found"
        const satsfactionsCaseNotFoundStatus =
            !judgmentStore.currentIntegration.cases.some((c) =>
                IntegrationCaseStatuses.CaseNotFound == c.status
            );

        //disable when every case is in a completed status
        const judgmentAllCreateCases = judgmentStore.currentIntegration.cases.every(
            (c) => disableCreateJudgmentsStatues.some((e) => e === c.status)
        );
        //disable where no cases match the "enable docket statuses"/"enable print statuses"
        const judgmentAllDocketDisabled =
            !judgmentStore.currentIntegration.cases.some((c) =>
                enableDocketStatuses.some((e) => e === c.status)
            );
        const judgmentsAllPrint = !judgmentStore.currentIntegration.cases.some(
            (c) => enablePrintExportStatuses.some((e) => e === c.status)
        );
        const judgmentsAllExport = !judgmentStore.currentIntegration.cases.some(
            (c) => enablePrintExportStatuses.some((e) => e === c.status)
        );

        const judgmentHeaderTemplate = () => {
            let printItems: MenuItem[] = [
                {
                    label: "Create Cases",
                    icon: "pi pi-plus",
                    command: () => {
                        createCasesFromJudgments();
                    },
                    disabled: judgmentAllCreateCases,
                },
                {
                    label: "Create Docket Events",
                    icon: "pi pi-file",
                    disabled: judgmentAllDocketDisabled,
                    command: () => {
                        generateDocketEntries(
                            "JudgmentIntegrationNotice",
                            "PraecipeToEnterJudment",
                            "JudgmentIntegrationCaseEventCategory",
                            "JudgmentIntegrationJudgmentPraecipeCaseEventCategory",
                            judgmentStore.currentIntegration?.id,
                            null
                        );
                    },
                },
                {
                    label: "Print Notices",
                    icon: "pi pi-print",
                    disabled: judgmentsAllPrint,
                    command: () => {
                        generateNotices(
                            "JudgmentIntegrationNotice",
                            "PraecipeToEnterJudment",
                            judgmentStore.currentIntegration?.id,
                            null,
                            judgmentStore.currentIntegration.name
                        );
                    },
                },
                {
                    label: "Export All Documents",
                    icon: "pi pi-download",
                    disabled: judgmentsAllExport,
                    command: () => {
                        downloadAllDocuments(
                            judgmentStore.currentIntegration.id,
                            "JudgmentIntegrationNotice",
                            "PraecipeToEnterJudment"
                        );
                    },
                },
            ];
            return (
                <>
                    <div className="header">
                        <div className="title">Import Results</div>
                        <Menu model={printItems} popup ref={headerMenu} id="print_menu" />
                        <Button
                            label="Actions"
                            className="p-button-sm"
                            onClick={(event) => headerMenu?.current?.toggle(event)}
                            icon="pi pi-angle-down"
                            iconPos="right"
                        />
                    </div>
                </>
            );
        };


        //disable when every case is in a completed status
        const satsfactionsAllCreateCases =
            judgmentStore.currentIntegration.cases.every((c) =>
                disableCreateSatisfactionsStatuses.some((e) => e === c.status)
            );
        //disable where no cases match the "enable docket statuses"/"enable print statuses"
        const satsfactionsAllDocketDisabled =
            !judgmentStore.currentIntegration.cases.some((c) =>
                enableDocketStatuses.some((e) => e === c.status)
            );
        const satsfactionsAllPrint = !judgmentStore.currentIntegration.cases.some(
            (c) => enablePrintExportStatuses.some((e) => e === c.status)
        );
        const satsfactionsAllExport = !judgmentStore.currentIntegration.cases.some(
            (c) => enablePrintExportStatuses.some((e) => e === c.status)
        );
        const satisfactionsHeaderTemplate = () => {
            let printItems: MenuItem[] = [
                {
                    label: "Apply Satisfactions",
                    icon: "pi pi-check",
                    command: () => {
                        generateSatisfactions();
                    },
                    disabled: satsfactionsAllCreateCases,
                    className: "create-satisfactions",
                }];

            var caseCreationEnabled = appSettingStore.settings.ApplicationSettings.find(p => p.Name == 'JudgmentIntegrationSatisfactionCaseCreationEnabled');
            if (caseCreationEnabled && caseCreationEnabled.Value == true) {
                printItems.push(
                    {
                        label: "Generate Missing Cases",
                        icon: "pi pi-plus",
                        command: () => {
                            createCaseFromSatisfaction(judgmentStore.currentIntegration.id, null);
                        },
                        disabled: satsfactionsCaseNotFoundStatus,
                        className: "create-cases",
                    });
            }
            printItems.push(...[
                {
                    label: "Create Docket Events",
                    icon: "pi pi-file",
                    disabled: satsfactionsAllDocketDisabled,
                    command: () => {
                        generateDocketEntries(
                            "SatisfactionIntegrationNotice",
                            "PraecipeToEnterSatisfaction",
                            "JudgmentIntegrationSatisfactionCaseEventCategory",
                            "JudgmentIntegrationSatisfactionPraecipeCaseEventCategory",
                            judgmentStore.currentIntegration?.id,
                            undefined
                        );
                    },
                },
                {
                    label: "Print Notices",
                    icon: "pi pi-print",
                    disabled: satsfactionsAllPrint,
                    command: () => {
                        generateNotices(
                            "SatisfactionIntegrationNotice",
                            "PraecipeToEnterSatisfaction",
                            judgmentStore.currentIntegration?.id,
                            null,
                            judgmentStore.currentIntegration.name
                        );
                    },
                },
                {
                    label: "Export All Documents",
                    icon: "pi pi-download",
                    disabled: satsfactionsAllExport,
                    command: () => {
                        downloadAllDocuments(
                            judgmentStore.currentIntegration.id,
                            "SatisfactionIntegrationNotice",
                            "PraecipeToEnterSatisfaction"
                        );
                    },
                },
            ]);
            return (
                <>
                    <div className="header">
                        <div className="title">Import Results</div>
                        <div className="p-mr-4">
                            <Menu model={printItems} popup ref={headerMenu} id="print_menu" />
                            <Button
                                label="Actions"
                                className="p-button-sm"
                                onClick={(event) => headerMenu?.current?.toggle(event)}
                                icon="pi pi-angle-down"
                                iconPos="right"
                            />
                        </div>
                    </div>
                </>
            );
        };

        const statuses = [
            { label: "Pending", value: 1 },
            { label: "Created", value: 2 },
            { label: "Docket Created", value: 7 },
            { label: "Case Found", value: 8 },
            { label: "Case Not Found", value: 3 },
            { label: "Multiple Cases Found", value: 11 },
            { label: "Failed to Create Case", value: 6 },
            { label: "Judgment Not Found", value: 4 },
            { label: "Multiple Judgments Found", value: 12 },
            { label: "Debtor Not Found", value: 5 },
            { label: "Primary Defendant Not Found", value: 9 },
            { label: "Multiple Primary Defendant Contacts Found", value: 10 },
        ];

        const statusTemplate = (value: JudgmentIntegrationCase) => {
            return (
                <>
                    <span className={`status-badge`} title={value.error}>
                        <span className={`${IntegrationCaseStatuses[value.status]}`}>
                            {value.statusMessage}
                        </span>
                    </span>
                </>
            );
        };

        const statusItemFilterTemplate = (label: any) => {
            let value: number = 0;

            var matchingStatuses = statuses.filter((p) => p.label === label);

            if (matchingStatuses.length > 0 && matchingStatuses[0].value != null)
                value = matchingStatuses[0].value;

            return (
                <span className={`status-badge`}>
                    <span className={`${IntegrationCaseStatuses[value]}`}>{label}</span>
                </span>
            );
        };
        const statusRowFilterTemplate = (options: any) => {
            return (
                <Dropdown
                    value={options.value}
                    options={statuses.map((p) => p.label)}
                    onChange={(e) => {
                        options.filterCallback(e.value, options.index);
                    }}
                    itemTemplate={statusItemFilterTemplate}
                    placeholder="Status"
                    className="p-column-filter"
                    style={{ width: "100%" }}
                />
            );
        };

        return (
            <>
                <OverlayPanel ref={aliasOverlayPanel} showCloseIcon dismissable>
                    <div
                        dangerouslySetInnerHTML={{ __html: aliasListConcatenated }}
                    ></div>
                </OverlayPanel>
                <div className="p-mt-4">
                    <Panel header={headerTemplate()}>
                        <div className="p-formgrid p-grid">
                            <div className="p-field p-col-12 p-md-12">
                                <label htmlFor="search" className="p-mr-2">
                                    Search:
                                </label>
                                <InputText
                                    value={globalFilterValue}
                                    onChange={onGlobalFilterChange}
                                    placeholder="Keyword Search"
                                    style={{ width: "50%" }}
                                />
                            </div>
                        </div>
                        <DataTable
                            dataKey="caseNumber"
                            value={judgmentStore.cases}
                            className="p-datatable-sm p-datatable-striped p-datatable-gridlines judgments"
                            emptyMessage="No Judgment Records Found"
                            ref={dtJudgment}
                            rowHover
                            paginator
                            filters={filters}
                            filterDisplay="menu"
                            responsiveLayout="scroll"
                            globalFilterFields={[
                                "caseNumber",
                                "civilCaseNumber",
                                "primaryDefendant.fullName",
                                "primaryDefendant.address.fullAddress",
                                "statusMessage",
                            ]}
                            /*without a sortField value the datagrid doesn't display, with a valid value you can't sort the init column without sorting another first, 
                            sorting works with an invalid value */
                            sortField="caseNumber2"
                            sortOrder={0}
                            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            rows={10}
                        >
                            <Column
                                field="caseNumber"
                                sortField="caseNumber"
                                sortable={true}
                                header="Reference Number"
                                style={{ width: "15%" }}
                                filter
                            />
                            <Column
                                field="civilCaseNumber"
                                sortField="civilCaseNumber"
                                sortable={true}
                                header="Case Number"
                                body={(rowData: any) => {
                                    return civilCaseNumberBodyTemplate(rowData);
                                }}
                                style={{ width: "15%" }}
                                filter
                            />
                            <Column
                                field="primaryDefendant.fullName"
                                header="Defendant"
                                sortField="primaryDefendant.fullName"
                                sortable={true}
                                body={(rowData: any) => {
                                    return defendantBodyTemplate(rowData);
                                }}
                                style={{ width: "10%" }}
                                filter
                            />
                            <Column
                                field="primaryDefendant.address.fullAddress"
                                sortField="primaryDefendant.address.fullAddress"
                                sortable={true}
                                header="Address"
                                style={{ width: "20%" }}
                                filter
                            />
                            <Column
                                field="judgments"
                                sortable={false}
                                header="Judgment"
                                body={(rowData: any) => {
                                    return judgmentBodyTemplate(rowData.judgments);
                                }}
                                style={{ width: "15%" }}
                            />
                            <Column
                                field="statusMessage"
                                sortField="statusMessage"
                                sortable={true}
                                filter={true}
                                filterElement={statusRowFilterTemplate}
                                header="Status"
                                body={statusTemplate}
                                style={{ width: "11%" }}
                            />
                            <Column
                                header="Actions"
                                body={(rowData: any) => {
                                    return actionsColumnTemplate(rowData);
                                }}
                                style={{ width: "12%" }}
                            />
                        </DataTable>
                    </Panel>
                </div>
            </>
        );
    }
);
